import {
  Dialog,
  DialogContent,
  DialogTrigger,
  Step,
  Steps,
  useToast,
} from '@ais-ui/ui-core';
import { ReactElement, useEffect } from 'react';
import { PiNotebook } from 'react-icons/pi';

import {
  Filter,
  ReportBackendTypes,
  useCreateReportTemplateAndReport,
} from '@ais-ui/websockets';
import { reportTypesObject } from '../../reportsTypes';
import { FilterStep } from './FilterStep';
import { NamingStep } from './NamingStep';
import { PeriodStep } from './PeriodStep';
import { ReportTypesStep } from './ReportTypesStep';
import { ShapeStep } from './ShapeStep';
import { MiscellaneousStep } from './MiscellaneousStep';
import { getReportParams } from './getReportParams';
import { useLocalization } from '@ais-ui/locale-management';
import { useCreateReportContext } from './CreateReportContext';
import { useDrawingContext } from '@ais-ui/react-openlayers';
import { useReportContext } from '../../ReportContext';
import { ShipStep } from './ShipStep';

interface CreateReportTemplateProps {
  children: ReactElement;
}

export const CreateReportTemplate = ({
  children,
}: CreateReportTemplateProps) => {
  const { messages } = useLocalization();
  const { mutate } = useCreateReportTemplateAndReport();
  const { toast } = useToast();

  const { geometryType } = useDrawingContext();
  const { setShouldRefetch } = useReportContext();
  const {
    showDialog,
    setShowDialog,
    setChosenReportType,
    steps,
    activeStep,
    chosenReportType,
    dateRange,
    reportTemplateName,
    firstReportName,
    shapeDictionary,
    filter,
    miscellaneous,
    resetSteps,
    currentStep,
    shapeTab,
    ships,
    shipAttribute,
  } = useCreateReportContext();
  useEffect(() => {
    if (activeStep !== steps.length) {
      return;
    }

    if (!chosenReportType) {
      return;
    }

    if (!dateRange || !dateRange.from || !dateRange.to) {
      return;
    }

    const params = getReportParams(
      chosenReportType,
      reportTemplateName,
      firstReportName,
      shapeDictionary,
      dateRange.from,
      dateRange.to,
      miscellaneous,
      filter,
      undefined,
      shapeTab,
      ships,
      shipAttribute
    );

    if (!params) {
      return;
    }

    const callbacks = {
      onSuccess: () => {
        toast({
          className: 'create-report-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsCreateStartedSuccessMessage,
        });
        setShowDialog(false);
        setShouldRefetch(true);
      },
      onError: () => {
        toast({
          className: 'create-report-error-toast',
          title: messages.genericToastTitleError,
          description: messages.reportsCreateStartedFailedMessage,
          variant: 'destructive',
        });
        setShowDialog(false);
      },
    };
    mutate({ type: chosenReportType.type, params }, callbacks);

    resetSteps();

    // We only want to run this if activeStep has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  let onPreviousNameStep:
    | ((event: React.MouseEvent<HTMLButtonElement>) => void)
    | null = null;

  if (chosenReportType !== reportTypesObject[ReportBackendTypes.REPLAY_VIDEO]) {
    onPreviousNameStep = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      resetSteps();
    };
  }

  useEffect(() => {
    // Do not reset when instant shape is being drawn
    if (!showDialog && !geometryType) {
      resetSteps();
    }
  }, [geometryType, showDialog]);

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="p-12 w-160 max-w-3xl min-w-md lg:h-[660px] 2xl:h-[760px] h-[500px]">
        <div className="flex flex-col h-[100%] overflow-hidden">
          <div className="pb-4 text-lg flex items-center justify-start space-x-4 h-12">
            <PiNotebook className="h-6 w-6 text-primary"></PiNotebook>
            <div className="text-muted-foreground">
              {chosenReportType
                ? messages.reportsCreateReportTypeTitle.replace(
                    '{label}',
                    chosenReportType.label
                  )
                : messages.reportsCreateReportChooseReportTypeTitle}
            </div>
          </div>
          {currentStep && (
            <Steps className="h-16 my-6" activeStep={activeStep}>
              {steps.map((step, index) => (
                <Step index={index} key={index} {...step}></Step>
              ))}
            </Steps>
          )}

          {!currentStep && (
            <ReportTypesStep
              setChosenReportType={setChosenReportType}
            ></ReportTypesStep>
          )}

          {currentStep?.label === 'Name' && (
            <NamingStep onPrevious={onPreviousNameStep}></NamingStep>
          )}
          {currentStep?.label === 'Period' && <PeriodStep></PeriodStep>}
          {currentStep?.label === 'Ships' && <ShipStep></ShipStep>}
          {currentStep?.label === 'Filter' && <FilterStep></FilterStep>}
          {currentStep?.label === 'Shape' && <ShapeStep></ShapeStep>}

          {currentStep?.label === 'Miscellaneous' && (
            <MiscellaneousStep></MiscellaneousStep>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
